import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { copy } from "../content/copy";
import { countryFlag } from "../lib/geo";
import { Link } from "../lib/router";
import HeroTally from "./HeroTally";
import { dailyNumber, dateKey, isPlayableKey, parseRecord, DAILY_RECORD_KEY } from "../experiences/daily/daily";

/**
 * Top of the country board as a one-line invitation into the Observatory.
 * One cheap fetch (the endpoint is edge-cached 10s); renders nothing until a
 * real country is on the board, so an empty board costs no layout.
 */
function StandingsTeaser() {
  const [flags, setFlags] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/stats");
        if (!res.ok) return;
        const body: unknown = await res.json();
        const countries = (body as { countries?: { c?: unknown; pops?: unknown }[] }).countries;
        if (cancelled || !Array.isArray(countries)) return;
        setFlags(
          countries
            .map((r) => r.c)
            .filter((c): c is string => typeof c === "string" && /^[A-Z]{2}$/.test(c) && c !== "XX")
            .slice(0, 3)
            .map(countryFlag),
        );
      } catch {
        /* stats down — the hero simply shows no teaser */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (flags.length === 0) return null;
  return (
    <Link to="/lab/observatory" className="hero-standings">
      <span aria-hidden="true">{flags.join(" ")}</span>
      {copy.hero.standings}
    </Link>
  );
}

/**
 * The Daily Pop's doorbell: today's sky is waiting — or, once played, the
 * Popper's own squares, a quiet reason to come back tomorrow. Reads the
 * device's record once on mount; nothing here touches the network.
 */
function DailyTeaser() {
  const today = dateKey(new Date());
  if (!isPlayableKey(today)) return null;
  let grid = "";
  try {
    grid = parseRecord(localStorage.getItem(DAILY_RECORD_KEY)).days[today]?.grid ?? "";
  } catch {
    /* storage blocked: show the invitation */
  }
  const n = dailyNumber(today);
  return (
    <Link to="/lab/daily" className="hero-standings hero-daily">
      {grid ? (
        <>
          {copy.hero.dailyDone.replace("{n}", String(n))}
          <span aria-label={grid}>{grid}</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">🎈</span>
          {copy.hero.daily.replace("{n}", String(n))}
        </>
      )}
    </Link>
  );
}

// Each balloon carries two positions: the wide-screen one and a narrow-screen
// one hugging the side walls, because on a phone the center of the hero belongs
// to the words. The CSS picks `--left-narrow`/`--top-narrow` under 640px.
const BALLOONS = [
  { left: "6%", top: "16%", nLeft: "-10%", nTop: "12%", size: 92, c1: "#ff3ea5", c2: "#b81f77", dur: "8s", delay: "0s" },
  { left: "84%", top: "12%", nLeft: "88%", nTop: "10%", size: 122, c1: "#35e0ff", c2: "#1786ad", dur: "11s", delay: "-3s" },
  { left: "78%", top: "64%", nLeft: "90%", nTop: "66%", size: 70, c1: "#ffe14d", c2: "#c9a616", dur: "7s", delay: "-1.5s" },
  { left: "10%", top: "64%", nLeft: "-14%", nTop: "62%", size: 106, c1: "#7c5cff", c2: "#4c34b0", dur: "10s", delay: "-5s" },
  { left: "44%", top: "7%", nLeft: "-8%", nTop: "38%", size: 56, c1: "#ff7a59", c2: "#c14a2e", dur: "9s", delay: "-2s" },
  { left: "62%", top: "78%", nLeft: "86%", nTop: "82%", size: 84, c1: "#5cffc0", c2: "#1fa678", dur: "12s", delay: "-6s" },
];

export default function Hero() {
  return (
    <header className="hero" id="top">
      {BALLOONS.map((b, i) => (
        <span
          key={i}
          className="balloon"
          aria-hidden="true"
          style={
            {
              "--left": b.left,
              "--top": b.top,
              "--left-narrow": b.nLeft,
              "--top-narrow": b.nTop,
              width: b.size,
              height: b.size * 1.12,
              background: `radial-gradient(circle at 32% 26%, rgba(255,255,255,.9) 0%, ${b.c1} 38%, ${b.c2} 100%)`,
              "--dur": b.dur,
              "--delay": b.delay,
            } as CSSProperties
          }
        />
      ))}
      <h1 className="wordmark">{copy.hero.title}</h1>
      <p className="tagline">{copy.hero.tagline}</p>
      <HeroTally />
      <div className="hero-pills">
        <DailyTeaser />
        <StandingsTeaser />
      </div>
      <p className="hero-sub">{copy.hero.sub}</p>
      <a className="cta" href="#playground">
        {copy.hero.cta}
      </a>
      <a className="scroll-cue" href="#playground" aria-hidden="true" tabIndex={-1}>
        {copy.hero.scrollCue} ↓
      </a>
    </header>
  );
}
